'use client';

import * as React from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import OutlinedInput from '@mui/material/OutlinedInput';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import ProfessionalDialog from '@/components/common/professional-dialog';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { Trash as TrashIcon } from '@phosphor-icons/react/dist/ssr/Trash';
import { Controller, useForm } from 'react-hook-form';
import { z as zod } from 'zod';

import { config } from '@/config';
import { logger } from '@/lib/default-logger';
import axios from '@/lib/v2/axios';
import { dayjs } from '@/lib/v2/dayjs';
import { toast } from '@/components/core/v2/toaster';
import axiosClient from '@/utils/client';

import type { Event, EventPriority } from './types';

// Add CalendarEventType interface
interface CalendarEventType {
  id: string;
  name: string;
  color: string;
}

export interface EventCreateParams {
  title: string;
  description?: string;
  allDay: boolean;
  start: Date;
  end: Date;
  priority?: EventPriority;
  type?: string;
  visibility?: 'public' | 'private';
}

export interface EventUpdateParams {
  title?: string;
  description?: string;
  start?: Date;
  end?: Date;
  allDay?: boolean;
  priority?: EventPriority;
  type?: string;
  visibility?: 'public' | 'private';
}

const schema = zod
  .object({
    title: zod.string().min(1, 'Title is required').max(255, 'Title must be less than 255 characters'),
    description: zod
      .string()
      .min(1, 'Description is required')
      .max(5000, 'Description must be less than 5000 characters'),
    start: zod.date({ required_error: 'Start date is required' }),
    end: zod.date({ required_error: 'End date is required' }),
    allDay: zod.boolean(),
    priority: zod.string().max(255).optional(),
    type: zod.string().optional(),
    visibility: zod.enum(['public', 'private']).optional(),
  })
  .refine((data) => data.start < data.end, {
    message: 'End date must be after start date',
    path: ['end'],
  });

type Values = zod.infer<typeof schema>;

function getDefaultValues({ event, range }: { event?: Event; range?: { start: Date; end: Date } }): Values {
  if (event) {
    return {
      title: event.title ?? '',
      description: event.description ?? '',
      start: event.start ? event.start : new Date(),
      end: event.end ? event.end : dayjs().add(30, 'minute').toDate(),
      allDay: event.allDay ?? false,
      priority: event.priority ?? 'low',
      type: event.type ?? '',
      visibility: event.visibility ?? 'public',
    };
  }

  if (range) {
    return { title: '', description: '', start: range.start, end: range.end, allDay: false, priority: 'low', type: '', visibility: 'public' };
  }

  return {
    title: '',
    description: '',
    start: new Date(),
    end: dayjs().add(30, 'minute').toDate(),
    allDay: false,
    priority: 'low',
    type: '',
    visibility: 'public',
  };
}

export interface EventDialogProps {
  action: 'create' | 'update';
  event?: Event;
  onClose?: () => void;
  onCreate?: (params: EventCreateParams) => void;
  onDelete?: (eventId: string) => void;
  onUpdate?: (eventId: string, params: EventUpdateParams) => void;
  open?: boolean;
  range?: { start: Date; end: Date };
}

export function EventDialog({
  action = 'create',
  event,
  onClose,
  onCreate,
  onDelete,
  onUpdate,
  open = false,
  range,
}: EventDialogProps): React.JSX.Element {
  const [calendarTypes, setCalendarTypes] = React.useState<CalendarEventType[]>([]);
  const [isLoadingTypes, setIsLoadingTypes] = React.useState(false);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<Values>({ defaultValues: getDefaultValues({ event, range }), resolver: zodResolver(schema) });

  // Fetch calendar event types
  React.useEffect(() => {
    const fetchCalendarTypes = async () => {
      try {
        setIsLoadingTypes(true);
        const response = await axiosClient.get('/api/v2/calendar/calendar-events?getTypes=1');
        setCalendarTypes(response.data);
      } catch (err) {
        logger.error(err);
        toast.error('Failed to load event types');
      } finally {
        setIsLoadingTypes(false);
      }
    };

    if (open) {
      fetchCalendarTypes();
    }
  }, [open]);

  const onSubmit = React.useCallback(
    async (values: Values): Promise<void> => {
      try {
        // Ensure description is not empty
        if (!values.description?.trim()) {
          toast.error('Description is required');
          return;
        }

        const params = {
          title: values.title.trim(),
          description: values.description.trim(),
          end: values.end,
          start: values.start,
          allDay: values.allDay,
          priority: values.priority as EventPriority,
          ...(values.type?.trim() ? { type: values.type.trim() } : {}),
          ...(values.visibility ? { visibility: values.visibility } : {}),
        } satisfies EventCreateParams | EventUpdateParams;

        if (action === 'update') {
          onUpdate?.(event!.id, params);
        } else {
          onCreate?.(params);
        }

        // Close dialog on success
        onClose?.();
      } catch (err) {
        logger.error(err);

        // Handle specific validation errors
        if (err instanceof Error) {
          if (err.message.includes('description')) {
            toast.error('Description is required');
          } else if (err.message.includes('title')) {
            toast.error('Title is required');
          } else {
            toast.error(err.message || 'Something went wrong!');
          }
        } else {
          toast.error('Something went wrong!');
        }
      }
    },
    [action, event, onCreate, onUpdate, onClose]
  );

  React.useEffect(() => {
    // Reset form when dialog data changes
    reset(getDefaultValues({ event, range }));
  }, [event, range, reset]);

  return (
    <ProfessionalDialog
      actions={
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
          {action === 'update' ? (
            <IconButton
              color="error"
              onClick={() => {
                onDelete?.(event!.id);
              }}
            >
              <TrashIcon />
            </IconButton>
          ) : (
            <span />
          )}
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <Button color="secondary" onClick={onClose} variant="outlined">
              Cancel
            </Button>
            <Button onClick={handleSubmit(onSubmit)} type="submit" variant="contained">
              {action === 'update' ? 'Save Changes' : 'Create Event'}
            </Button>
          </Stack>
        </Stack>
      }
      maxWidth="sm"
      onClose={onClose || (() => {})}
      open={Boolean(open)}
      subtitle={event ? 'Update event details' : 'Add a new calendar event'}
      title={event ? 'Edit Event' : 'Create Event'}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={4}>
          {/* Basic Information Section */}
          <Stack spacing={3}>
            <Typography color="text.primary" sx={{ fontWeight: 600, mb: 1 }} variant="h6">
              Event Details
            </Typography>
            
            <Controller
              control={control}
              name="title"
              render={({ field }) => (
                <FormControl error={Boolean(errors.title)} fullWidth>
                  <InputLabel>Title *</InputLabel>
                  <OutlinedInput {...field} label="Title *" placeholder="Enter event title..." />
                  {errors.title ? <FormHelperText>{errors.title.message}</FormHelperText> : null}
                </FormControl>
              )}
            />
            
            <Controller
              control={control}
              name="description"
              render={({ field }) => (
                <FormControl error={Boolean(errors.description)} fullWidth>
                  <InputLabel>Description *</InputLabel>
                  <OutlinedInput
                    {...field}
                    label="Description *"
                    multiline
                    placeholder="Enter event description..."
                    rows={4}
                  />
                  {errors.description ? <FormHelperText>{errors.description.message}</FormHelperText> : null}
                </FormControl>
              )}
            />
          </Stack>

          {/* Event Settings Section */}
          <Stack spacing={3}>
            <Typography color="text.primary" sx={{ fontWeight: 600, mb: 1 }} variant="h6">
              Event Settings
            </Typography>
            
            <Stack direction="row" spacing={2}>
              <Controller
                control={control}
                name="type"
                render={({ field }) => (
                  <FormControl error={Boolean(errors.type)} fullWidth>
                    <InputLabel>Type</InputLabel>
                    <Select {...field} disabled={isLoadingTypes} label="Type">
                      <MenuItem value="">
                        <em>Select a type</em>
                      </MenuItem>
                      {calendarTypes.map((type) => (
                        <MenuItem key={type.id} value={type.name}>
                          {type.name}
                        </MenuItem>
                  ))}
                    </Select>
                    {errors.type ? <FormHelperText>{errors.type.message}</FormHelperText> : null}
                  </FormControl>
                )}
              />
              
              <Controller
                control={control}
                name="visibility"
                render={({ field }) => (
                  <FormControl error={Boolean(errors.visibility)} fullWidth>
                    <InputLabel>Visibility</InputLabel>
                    <Select {...field} label="Visibility">
                      <MenuItem value="public">Public (visible to all members)</MenuItem>
                      <MenuItem value="private">Private (managers/admins only)</MenuItem>
                    </Select>
                    {errors.visibility ? <FormHelperText>{errors.visibility.message}</FormHelperText> : null}
                  </FormControl>
                )}
              />
            </Stack>
            
            <Controller
              control={control}
              name="allDay"
              render={({ field }) => (
                <Box sx={{ pt: 1 }}>
                  <FormControlLabel 
                    control={<Checkbox {...field} checked={field.value} />} 
                    label="All day event" 
                    sx={{ 
                      '& .MuiFormControlLabel-label': { 
                        fontWeight: 500,
                        color: 'text.primary'
                      } 
                    }}
                  />
                  {errors.allDay ? <FormHelperText error>{errors.allDay.message}</FormHelperText> : null}
                </Box>
              )}
            />
          </Stack>

          {/* Date & Time Section */}
          <Stack spacing={3}>
            <Typography color="text.primary" sx={{ fontWeight: 600, mb: 1 }} variant="h6">
              Date & Time
            </Typography>
            
            <Controller
              control={control}
              name="start"
              render={({ field }) => (
                <DateTimePicker
                  format="MMM D, YYYY hh:mm A"
                  label="Start date *"
                  onChange={(date) => {
                    field.onChange(date ? date.toDate() : null);
                  }}
                  slotProps={{
                    textField: {
                      error: Boolean(errors.start),
                      helperText: errors.start?.message,
                      fullWidth: true,
                    },
                  }}
                  value={dayjs(field.value)}
                />
              )}
            />
            
            <Controller
              control={control}
              name="end"
              render={({ field }) => (
                <DateTimePicker
                  format="MMM D, YYYY hh:mm A"
                  label="End date *"
                  onChange={(date) => {
                    field.onChange(date ? date.toDate() : null);
                  }}
                  slotProps={{
                    textField: {
                      error: Boolean(errors.end),
                      helperText: errors.end?.message,
                      fullWidth: true,
                    },
                  }}
                  value={dayjs(field.value)}
                />
              )}
            />
          </Stack>
        </Stack>
      </form>
    </ProfessionalDialog>
  );
}
